<script setup lang="ts">
import { computed, ref } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EDataUnit } from "@/constants/data-units.type";
import { useTypedIntl } from "@/locales/typed-intl";
import { cn } from "@/utils/libs/tailwind-utils";
import { getNumberWithSign } from "@/utils/number";
import { isDefined } from "@/utils/object";
import { getValueClassColor } from "@/utils/style-utils";

import ConsumptionHighVariationDialog from "@/app/data/dashboard/dialog/consumption-high-variation-dialog.vue";
import InsightsCard from "@/components/data/dashboard/insights-card.vue";

interface IProps {
  isLoading?: boolean;
  value: number | undefined | null;
  title: string;
}

const props = defineProps<IProps>();
const infosVisible = defineModel<boolean>("infosVisible");

const { t, k } = useTypedIntl();

const isHighVariationDialogVisible = ref<boolean>(false);

const HIGH_CONSUMPTION_EVOLUTION_THRESHOLD = 200;

const isConsumptionEvolutionTooHigh = computed<boolean>(() => {
  const consumptionEvolution = props.value;
  if (isDefined(consumptionEvolution)) {
    return consumptionEvolution > HIGH_CONSUMPTION_EVOLUTION_THRESHOLD;
  }
  return false;
});
</script>

<template>
  <InsightsCard
    v-model:infos-visible="infosVisible"
    :is-loading="props.isLoading"
    :title="props.title"
    v-bind="$attrs"
  >
    <span :class="cn('text-2xl font-semibold whitespace-nowrap', getValueClassColor(props.value))">
      {{ t(k.global.units[EDataUnit.PERCENT]({ value: getNumberWithSign(props.value) })) }}
    </span>
    <template #bottom>
      <a
        v-if="isConsumptionEvolutionTooHigh"
        class="text-orange-500 cursor-pointer"
        @click="isHighVariationDialogVisible = true"
        :data-test-id="DATA_TEST_IDS.views.dashboard.consumptionHighVariation.button"
        aria-haspopup="dialog"
        aria-controls="consumption-high-variation-dialog"
        :aria-expanded="!!isConsumptionEvolutionTooHigh"
        tabindex="0"
        aria-label="show-consumption-high-variation-warning-button"
      >
        <i class="pi pi-exclamation-triangle !text-xs pr-2" />
        {{ t(k.data.dashboard.insights.consumptionEvolution.highVariation.warning) }}
      </a>

      <div
        v-else
        class="text-xs text-text-secondary flex items-center gap-2"
      >
        <i class="pi pi-arrow-right-arrow-left !text-xs" />

        <span>{{ t(k.data.dashboard.insights.consumptionEvolution.subtitle) }}</span>
      </div>
    </template>

    <template #infos>
      <slot name="infos" />
    </template>
  </InsightsCard>

  <ConsumptionHighVariationDialog
    v-model="isHighVariationDialogVisible"
    id="consumption-high-variation-dialog"
  />
</template>
